import { useEffect } from "react";
import { Users, Calendar, DollarSign, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { siteConfig } from "@/config/siteConfig";

export default function BusHire() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="relative py-20 bg-gradient-to-b from-primary/10 to-background">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6">
            <span>← Back to Home</span>
          </Link>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Bus <span className="text-gradient">Hire</span> Services
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl">
            Premium coach rental for events, group travel, and corporate needs.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          {/* Left Column */}
          <div>
            <h2 className="font-display text-3xl font-bold mb-6">Hire a Coach for Your Event</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Perfect for Groups</h3>
                  <p className="text-muted-foreground">
                    From 20 to 60+ passengers. Our modern fleet can accommodate groups of any size.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Flexible Scheduling</h3>
                  <p className="text-muted-foreground">
                    Available for one-time rentals or recurring scheduled services that fit your needs.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Premium Comfort</h3>
                  <p className="text-muted-foreground">
                    Air conditioning, entertainment systems, and reclining seats for passenger comfort.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Competitive Rates</h3>
                  <p className="text-muted-foreground">
                    Transparent pricing with volume discounts for long-term bookings and recurring services.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <div className="bg-card rounded-2xl p-8 border border-border/50">
              <h3 className="font-display text-2xl font-bold mb-6">Popular Uses</h3>
              <div className="space-y-4">
                <div className="pb-4 border-b border-border/50">
                  <h4 className="font-semibold mb-2">Corporate Events</h4>
                  <p className="text-sm text-muted-foreground">
                    Team outings, business conferences, and executive transport.
                  </p>
                </div>
                <div className="pb-4 border-b border-border/50">
                  <h4 className="font-semibold mb-2">Group Travel</h4>
                  <p className="text-sm text-muted-foreground">
                    Family reunions, school trips, and tourist group travel.
                  </p>
                </div>
                <div className="pb-4 border-b border-border/50">
                  <h4 className="font-semibold mb-2">Special Events</h4>
                  <p className="text-sm text-muted-foreground">
                    Weddings, celebrations, and private functions transport.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Airport Transfers</h4>
                  <p className="text-sm text-muted-foreground">
                    Group airport transportation with professional service.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8">
              <h3 className="font-semibold mb-4">What's Included</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  Professional driver
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  Fuel and maintenance
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  All amenities
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  Insurance coverage
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  24/7 support
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Fleet Info */}
        <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-12 mb-16">
          <h2 className="font-display text-3xl font-bold mb-8 text-center">Our Fleet</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-lg mb-3">Coach Specifications</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Seating capacity: 40-60 passengers</li>
                <li>• Modern safety features</li>
                <li>• Air conditioning and heating</li>
                <li>• Entertainment system</li>
                <li>• USB charging ports</li>
                <li>• Wheelchair accessibility available</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-3">Driver Services</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Licensed and insured drivers</li>
                <li>• Professional and courteous service</li>
                <li>• Cross-border experience</li>
                <li>• Flexible routing options</li>
                <li>• Safety compliance certified</li>
                <li>• Customer support available</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-12">
          <h2 className="font-display text-3xl font-bold mb-4">Ready to Book Your Bus?</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Contact us for an instant quote. We'll work with you to create a customized package for your group travel needs.
          </p>
          <Button asChild size="lg" className="text-lg px-8">
            <a href={`mailto:${siteConfig.email}?subject=Bus%20Hire%20Quote%20Request&body=Hello%20CityLink%2C%0A%0AI%20am%20interested%20in%20hiring%20a%20bus%20for%20my%20group.%20Please%20provide%20me%20with%20a%20quote.%0A%0AThank%20you`}>
              Request a Quote Now
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
